import type { FormValidationRule } from '@vuetify/v0'
import type { ComputedRef, Ref } from 'vue'

export type ValidationResult = string | boolean
export type ValidationRule = FormValidationRule

export type ReactiveForm<T, S = Record<string, unknown>> = {
  valid: boolean
  disabled?: boolean
  loading?: boolean
  model: T
  rules: Record<keyof T | string, ValidationRule[] | ComputedRef<ValidationRule[]>>
  label?: Record<string, string | ComputedRef<string>>
  state: S
}

const text = (value: unknown) => (value === null || value === undefined ? '' : String(value))

const unref = <T>(source: Ref<T> | T | undefined): T | undefined =>
  source && typeof source === 'object' && 'value' in source ? source.value : source

const files = (value: unknown): File[] => (Array.isArray(value) ? value : value instanceof File ? [value] : [])

const error = (message?: string) => message ?? ''

const luhnCheck = (value: string) => {
  let sum = 0
  for (let i = 0; i < value.length; i++) {
    let digit = Number.parseInt(value.charAt(i), 10)
    if (i % 2 === 0) {
      digit *= 2
      if (digit > 9) {
        digit = 1 + (digit % 10)
      }
    }
    sum += digit
  }
  return sum % 10 === 0
}

export const patternRule =
  (pattern: RegExp, message?: string): ValidationRule =>
  (value: unknown) =>
    !text(value).length || pattern.test(text(value)) || error(message)

export const requiredRule =
  (message?: string): ValidationRule =>
  (value: unknown) =>
    !!value || error(message)

export const checkedRule =
  (message?: string): ValidationRule =>
  (value: unknown) =>
    value === true || error(message)

export const minRule =
  (min?: number, message?: string): ValidationRule =>
  (value: unknown) =>
    Number(value) >= (min ?? 0) || error(message)

export const maxRule =
  (max: number, message?: string): ValidationRule =>
  (value: unknown) =>
    Number(value) <= max || error(message)

export const positiveRule =
  (message?: string): ValidationRule =>
  (value: unknown) =>
    Number(value) > 0 || error(message)

export const dateRangeRule =
  (start: Ref<string | Date | undefined> | string | Date | undefined, message?: string): ValidationRule =>
  (value: unknown) => {
    const from = unref(start)
    if (!value || !from) return true
    return new Date(value as string | Date) >= new Date(from) || error(message)
  }

export const minLengthRule =
  (min: number, message?: string): ValidationRule =>
  (value: unknown) =>
    !value || text(value).length >= min || error(message)

export const maxLengthRule =
  (max: number, message?: string): ValidationRule =>
  (value: unknown) =>
    !value || text(value).length <= max || error(message)

export const emailRule = (message?: string): ValidationRule => patternRule(/^\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]+\b$/, message)

export const digitRule = (message?: string): ValidationRule => patternRule(/^(?=.*?\d).*$/, message)

export const capitalRule = (message?: string): ValidationRule => patternRule(/^(?=.*?[A-ZÄÖÜ]).*$/, message)

export const lowercaseRule = (message?: string): ValidationRule => patternRule(/^(?=.*?[a-zäöüß]).*$/, message)

export const allowedRule = (message?: string): ValidationRule => patternRule(/^[a-zA-Z0-9!@#$%^*()_\-+{};:.,?§°€µ¢£¥~<>äöüßÄÖÜ]+$/, message)

export const noSpecialCharsRule = (message?: string): ValidationRule => patternRule(/^[^/]*$/, message)

export const symbolRule = (message?: string): ValidationRule => patternRule(/^(?=.*?[!@#$%^*()_\-+{};:.,?§°€µ¢£¥~<>]).*$/, message)

export const identicalRule =
  (field: Ref<string | undefined> | string | undefined, message?: string): ValidationRule =>
  (value: unknown) =>
    unref(field) === value || error(message)

export const ccNumberRule =
  (message?: string): ValidationRule =>
  (value: unknown) =>
    (/^\d{15,16}$/.test(text(value)) && luhnCheck(text(value))) || error(message)

export const ccCSCRule = (message?: string): ValidationRule => patternRule(/^\d{3,4}$/, message)

export const ccMonthRule =
  (message?: string): ValidationRule =>
  (value: unknown) => {
    const month = Number.parseInt(text(value), 10)
    return (month > 0 && month < 13) || error(message)
  }

export const ccYearRule =
  (message?: string): ValidationRule =>
  (value: unknown) =>
    Number.parseInt(text(value), 10) >= new Date().getFullYear() || error(message)

export const maxFileSizeRule =
  (maxSize: number, message?: string): ValidationRule =>
  (value: unknown) =>
    files(value).every(file => file.size <= maxSize) || error(message)

const matchesType = (file: File, type: string) => (type.endsWith('*') ? file.type.startsWith(type.replace('*', '')) : file.type === type)

export const fileTypeRule =
  (allowedTypes: string[], message?: string): ValidationRule =>
  (value: unknown) =>
    files(value).every(file => allowedTypes.some(type => matchesType(file, type))) || error(message)
